import { useNavigate, useParams } from "#routers/hooks";
import { linkToDeviceDetail, linkToFacilities } from "#routers/links";
import {
  AssetCreateMode,
  AssetCreateModeType,
} from "#src/batteries-included-components/Buttons/CreateAssetDropdownMenu/CreateAssetDropdownMenu";
import {
  DEVICE_QUERY_KEY,
  useListDeviceTypes,
} from "#src/components/hooks/adapters/utils/useDevices";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import {
  MultiStepFormProvider,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { DevicesListRoutePath } from "#src/routes/organization/devices";
import { DevicesCreateRoutePath } from "#src/routes/organization/devices/create";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { useQueryClient } from "@tanstack/react-query";
import { Page, Panel, useToast } from "@validereinc/common-components";
import {
  AssetType,
  DeviceAdapter,
  DeviceSchemaEnumKeys,
  DeviceTypeType,
  Resources,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import { isNil, pick } from "es-toolkit/compat";
import React from "react";
import styles from "./CreateDevicePage.module.css";
import { DeviceDetailsFormStep } from "./DeviceDetailsFormStep";
import { DeviceOverviewFormStep } from "./DeviceOverviewFormStep";

const cx = classNames.bind(styles);

const CreateDevicePageContent = ({
  assetCreateMode,
  deviceTypes,
  isLoadingDeviceTypes,
}: {
  assetCreateMode: AssetCreateModeType;
  deviceTypes: DeviceTypeType[];
  isLoadingDeviceTypes: boolean;
}) => {
  const [areTemplatedConfigurationsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:templated_configurations",
  });
  const [breadcrumbs] = useBreadcrumbsFromRoute(DevicesCreateRoutePath);
  const { stepper, footer } = useMultiStepFormContext();
  const { localize } = useLocalization();

  const shouldUseAssetBuilder =
    areTemplatedConfigurationsAvailable &&
    assetCreateMode === AssetCreateMode.ASSET_BUILDER;

  return (
    <Page
      title={localize(DevicesCreateRoutePath.title)}
      category={
        shouldUseAssetBuilder
          ? "Asset Builder"
          : `Quick Create ${localize(AssetType.DEVICE)}`
      }
      shouldCenterContent
      breadcrumbs={breadcrumbs}
      footer={footer}
      footerAlign="space-between"
    >
      <Panel
        className={cx("container")}
        isFluidY={false}
      >
        <div className={cx("stepper-container")}>{stepper}</div>
        <div className={cx("form-container")}>
          <DeviceOverviewFormStep
            shouldUseAssetBuilder={shouldUseAssetBuilder}
            deviceTypes={deviceTypes}
            isLoadingDeviceTypes={isLoadingDeviceTypes}
          />
          <DeviceDetailsFormStep deviceTypes={deviceTypes} />
        </div>
      </Panel>
    </Page>
  );
};

export const CreateDevicePage = () => {
  const { localize } = useLocalization();
  const navigate = useNavigate();
  const { mode } = useParams<{
    mode: AssetCreateModeType;
  }>();
  const urlSearchParams = new URLSearchParams(document.location.search);
  const facilityId = urlSearchParams.get("facilityId");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: deviceTypesData, isLoading: isLoadingDeviceTypes } =
    useListDeviceTypes(undefined);
  const deviceTypes = deviceTypesData?.data ?? [];

  const redirectAfterSuccess = (deviceId: string) => {
    if (facilityId) {
      navigate({
        pathname: linkToFacilities(facilityId),
        query: { section: "devices" },
      });
    } else {
      navigate({ pathname: linkToDeviceDetail(deviceId) });
    }
  };

  const handleSubmit = async (formValues: any) => {
    const allValues = formValues.reduce(
      (total, current) => ({ ...total, ...current }),
      {}
    );

    const locationData = allValues.attributes?.location;
    const selectedDeviceTypeName = deviceTypes?.find(
      (deviceType) => deviceType.id === formValues[DeviceSchemaEnumKeys.type_id]
    )?.name;

    if (
      (isNil(locationData?.[0]) || locationData?.[0] === "") &&
      (isNil(locationData?.[1]) || locationData?.[1] === "")
    ) {
      allValues.attributes.location = null;
    }

    try {
      let body = {
        ...allValues,
        status: allValues[DeviceSchemaEnumKeys.status].toLowerCase(),
      };

      body = pick(body, [
        "facility_id",
        "name",
        "type_id",
        "status",
        "measurement_types",
        "attributes",
        "custom_attributes",
      ]);

      const { data } = await DeviceAdapter.createOne({ data: body });
      toast.push({
        intent: "success",
        description: `Successfully created new ${selectedDeviceTypeName ?? localize(Resources.DEVICE)} "${data.name}".`,
      });
      redirectAfterSuccess(data.id);
      queryClient.invalidateQueries({
        queryKey: [DEVICE_QUERY_KEY],
      });
      queryClient.refetchQueries({
        queryKey: [DEVICE_QUERY_KEY],
      });
    } catch {
      toast.push({
        intent: "error",
        description: `Unable to create ${selectedDeviceTypeName ?? localize(Resources.DEVICE)} "${formValues.name}".`,
      });
    }
  };

  const handleCancelClick = () => {
    navigate(DevicesListRoutePath.toLinkParts());
  };

  return (
    <MultiStepFormProvider
      steps={[
        {
          label: "Overview",
        },
        {
          label: "Details",
        },
      ]}
      orientation="vertical"
      onCancel={handleCancelClick}
      onSubmit={handleSubmit}
      submissionActionText="Create"
      stepperClassName={cx("sticky-stepper")}
    >
      <CreateDevicePageContent
        assetCreateMode={mode}
        deviceTypes={deviceTypes}
        isLoadingDeviceTypes={isLoadingDeviceTypes}
      />
    </MultiStepFormProvider>
  );
};
